/**
 * DUNGEON BIOMES - World map tiles that spawn dungeons
 *
 * These biomes trigger dungeon entry instead of normal tile exploration.
 * Each dungeon biome has a specific difficulty tier, mob pool, and loot table.
 */

// ============================================================================
// DUNGEON BIOME DEFINITIONS
// ============================================================================

export const DUNGEON_BIOMES = {
  // ═══════════════════════════════════════════════════════════════════════════
  // TIER 1 DUNGEONS - Beginner Content (Age 1+)
  // ═══════════════════════════════════════════════════════════════════════════
  ancientRuins: {
    id: 'ancientRuins',
    name: 'Ancient Ruins',
    icon: '🏚️',
    description: 'Crumbling stonework hides dangerous passages beneath. A dungeon entrance beckons.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#4a4a3a',
    minAge: 1,
    spawnWeight: 8,

    // Dungeon-specific properties
    isDungeonBiome: true,
    dungeonTier: 'tier1', // Easy - beginner dungeon
    cooldownMinutes: 10, // Minimum 10 minutes before re-entry

    // Mob pool: BALANCED for new players (30-40 HP, 5-8 ATK)
    // - Goblins: Fast but weak (30 HP, 5 ATK)
    // - Skeletons: Glass cannon (25 HP, 7 ATK)
    // - Wolves: Tanky basic mob (40 HP, 8 ATK)
    dungeonMobPool: ['goblin', 'skeleton', 'wolf'],

    // Loot table: 70% common, 25% uncommon, 5% rare
    // Expected drops: Leather armor, rusty weapons, basic rings
    dungeonLootTable: 'common',

    // Boss: Ogre Chieftain (200 HP, 20 ATK, 10 DEF)
    // Beatable with common gear + smart play
    dungeonBossPool: ['boss_ogre'],

    // Entry events (shown when discovering the biome tile, not entering dungeon)
    entryEvents: [
      { id: 'ominous', weight: 30, text: 'Dark passages descend into the earth. You sense danger... and treasure.' },
      { id: 'entrance_found', weight: 40, text: 'A dungeon entrance! Faded carvings warn of trials within.', rewards: { knowledge: 5 } },
      { id: 'old_bones', weight: 20, text: 'Adventurer bones litter the entrance. They were not prepared.', rewards: { gold: 3 } },
      { id: 'collapse', weight: 10, text: 'Part of the ruins collapses as you approach!', penalties: { food: 5 } },
    ],

    gatherableResources: {
      stone: { baseAmount: 3, richness: 0.5 },
      knowledge: { baseAmount: 2, richness: 0.3 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  goblinCave: {
    id: 'goblinCave',
    name: 'Goblin Cave',
    icon: '🕳️',
    description: 'A dank cave network infested with goblins. Their crude traps litter the tunnels.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#3a4a3a',
    minAge: 1,
    spawnWeight: 7,

    // Dungeon-specific properties
    isDungeonBiome: true,
    dungeonTier: 'tier1',
    cooldownMinutes: 10,

    // Mob pool: Goblin-heavy with wolf companions
    // - Goblins: Main threat, fast and numerous
    // - Wolves: Goblin pets, tanky support
    // Mob pool is now determined by level variant in DUNGEON_MOB_POOLS
    dungeonMobPool: ['goblinWhelp', 'caveBat', 'goblinScout', 'goblinBrute'],

    dungeonLootTable: 'common',

    // Boss: Goblin King - the ruler of the cave
    dungeonBossPool: ['boss_goblinKing'],

    entryEvents: [
      { id: 'goblin_noise', weight: 35, text: 'Cackling echoes from the darkness. The goblins know you\'re here.' },
      { id: 'stolen_goods', weight: 30, text: 'Stolen merchant goods pile near the entrance!', rewards: { gold: 8 } },
      { id: 'crude_trap', weight: 20, text: 'A crude goblin trap springs! Painful but not deadly.', penalties: { food: 4 } },
      { id: 'goblin_scout', weight: 15, text: 'A goblin scout spots you and flees deeper into the cave.' },
    ],

    gatherableResources: {
      stone: { baseAmount: 2, richness: 0.4 },
      iron: { baseAmount: 1, richness: 0.2 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  banditHideout: {
    id: 'banditHideout',
    name: 'Bandit Hideout',
    icon: '⚔️',
    description: 'A fortified camp where outlaws stash their ill-gotten gains. Human foes await.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#4a3a3a',
    minAge: 1,
    spawnWeight: 6,

    // Dungeon-specific properties
    isDungeonBiome: true,
    dungeonTier: 'tier1',
    cooldownMinutes: 10,

    // Mob pool: Human outlaws and their minions
    // Mob pool is now determined by level variant in DUNGEON_MOB_POOLS
    dungeonMobPool: ['banditThug', 'guardDog', 'banditArcher', 'banditVeteran'],

    dungeonLootTable: 'common',

    // Boss: Bandit Lord - the leader of the outlaws
    dungeonBossPool: ['boss_banditLord'],

    entryEvents: [
      { id: 'watchfire', weight: 30, text: 'A dying watchfire marks the hideout entrance. They\'ve grown careless.' },
      { id: 'loot_cache', weight: 35, text: 'An unguarded loot cache near the entrance!', rewards: { gold: 10, food: 3 } },
      { id: 'tripwire', weight: 20, text: 'You trigger a tripwire alarm! Bandits stir within.', penalties: { food: 3 } },
      { id: 'wanted_poster', weight: 15, text: 'Wanted posters flutter in the wind. These are dangerous men.', rewards: { knowledge: 3 } },
    ],

    gatherableResources: {
      gold: { baseAmount: 3, richness: 0.5 },
      food: { baseAmount: 2, richness: 0.3 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  hauntedMine: {
    id: 'hauntedMine',
    name: 'Haunted Mine',
    icon: '⛏️',
    description: 'An abandoned mine where restless spirits of dead miners still toil. Rich in ore but cursed.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#3a3a4a',
    minAge: 1,
    spawnWeight: 5,

    // Dungeon-specific properties
    isDungeonBiome: true,
    dungeonTier: 'tier1',
    cooldownMinutes: 10,

    // Mob pool: Undead miners and cave creatures
    // Mob pool is now determined by level variant in DUNGEON_MOB_POOLS
    dungeonMobPool: ['mineRat', 'undeadMiner', 'phantomMiner', 'minerWraith'],

    dungeonLootTable: 'common',

    // Boss: Spectral Overseer - the ghost of the mine foreman
    dungeonBossPool: ['boss_mineOverseer'],

    entryEvents: [
      { id: 'mining_sounds', weight: 30, text: 'Phantom pickaxes echo from below. The dead still dig.' },
      { id: 'ore_vein', weight: 35, text: 'A rich ore vein glitters near the entrance!', rewards: { iron: 5, stone: 3 } },
      { id: 'cave_in', weight: 20, text: 'Unstable timbers creak overhead. Part of the ceiling falls!', penalties: { food: 5 } },
      { id: 'miner_ghost', weight: 15, text: 'A spectral miner points deeper into the tunnels, then vanishes.', rewards: { knowledge: 4 } },
    ],

    gatherableResources: {
      stone: { baseAmount: 4, richness: 0.6 },
      iron: { baseAmount: 2, richness: 0.4 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // TIER 2 DUNGEONS - Intermediate Content (Age 3+)
  // ═══════════════════════════════════════════════════════════════════════════
  darkCatacombs: {
    id: 'darkCatacombs',
    name: 'Dark Catacombs',
    icon: '⚰️',
    description: 'A tomb complex where the dead refuse to rest. Stronger foes and richer rewards await.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#2a2a3a',
    minAge: 3,
    spawnWeight: 5,

    // Dungeon-specific properties
    isDungeonBiome: true,
    dungeonTier: 'tier2', // Medium - requires uncommon gear
    cooldownMinutes: 20, // 20 minutes cooldown for tier 2

    // Mob pool: BALANCED for mid-game (45-60 HP, 12-15 ATK, 5× tier scaling)
    // - Skeletons: Familiar enemy, scaled up (25→43 HP, 7→11 ATK)
    // - Wraiths: Ranged threat (45→77 HP, 15→24 ATK, range 2)
    // - Orcs: Tanky frontline (60→103 HP, 12→19 ATK, 6 DEF)
    // Mix of familiar + new enemies for learning curve
    dungeonMobPool: ['skeleton', 'wraith', 'orc', 'darkKnight'],

    // Loot table: 40% common, 40% uncommon, 18% rare, 2% epic
    // Expected drops: Steel weapons, chainmail, silver rings
    dungeonLootTable: 'uncommon',

    // Boss: Ancient Lich (150→258 HP, 25→40 ATK, 5 DEF, range 3)
    // Dangerous ranged boss - requires uncommon gear + positioning
    dungeonBossPool: ['boss_lich'],

    entryEvents: [
      { id: 'cursed', weight: 25, text: 'The air here is thick with dark magic. The dead stir below.' },
      { id: 'entrance_sealed', weight: 30, text: 'Ancient seals guard the catacombs. You break through.', rewards: { knowledge: 10 } },
      { id: 'grave_goods', weight: 25, text: 'Burial offerings near the entrance!', rewards: { gold: 12, gems: 1 } },
      { id: 'spectral_attack', weight: 15, text: 'Wraiths pour from the entrance!', penalties: { food: 8 } },
      { id: 'curse_trap', weight: 5, text: 'A death curse strikes you!', penalties: { food: 12, magic: 3 } },
    ],

    gatherableResources: {
      knowledge: { baseAmount: 4, richness: 0.6 },
      magic: { baseAmount: 2, richness: 0.4 },
      gold: { baseAmount: 3, richness: 0.5 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  frozenCrypt: {
    id: 'frozenCrypt',
    name: 'Frozen Crypt',
    icon: '❄️',
    description: 'An ancient burial site locked in eternal winter. Ice-bound horrors stir within.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#3a4a5a',
    minAge: 3,
    spawnWeight: 4,

    isDungeonBiome: true,
    dungeonTier: 'tier2',
    cooldownMinutes: 20,

    // Mob pool: Ice undead and frost creatures
    dungeonMobPool: ['frostZombie', 'iceSpider', 'frozenWight', 'glacialGolem'],
    dungeonLootTable: 'uncommon',
    dungeonBossPool: ['boss_frostLich'],

    entryEvents: [
      { id: 'bitter_cold', weight: 30, text: 'The cold cuts to the bone. Your breath crystallizes instantly.' },
      { id: 'frozen_treasure', weight: 25, text: 'Treasure preserved in ice near the entrance!', rewards: { gold: 15, gems: 1 } },
      { id: 'ice_trap', weight: 20, text: 'Ice forms beneath your feet, slowing your approach!', penalties: { food: 6 } },
      { id: 'preserved_library', weight: 15, text: 'Frozen scrolls contain ancient knowledge!', rewards: { knowledge: 12 } },
      { id: 'hypothermia', weight: 10, text: 'The cold saps your strength!', penalties: { food: 10 } },
    ],

    gatherableResources: {
      crystals: { baseAmount: 3, richness: 0.5 },
      knowledge: { baseAmount: 3, richness: 0.4 },
      magic: { baseAmount: 2, richness: 0.3 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  floodedSewers: {
    id: 'floodedSewers',
    name: 'Flooded Sewers',
    icon: '🐀',
    description: 'Fetid tunnels beneath a ruined city. Disease and monstrosities lurk in the muck.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#3a4a3a',
    minAge: 3,
    spawnWeight: 4,

    isDungeonBiome: true,
    dungeonTier: 'tier2',
    cooldownMinutes: 20,

    // Mob pool: Plague creatures and sewer dwellers
    dungeonMobPool: ['giantRat', 'sewerSlime', 'plagueZombie', 'ratKing'],
    dungeonLootTable: 'uncommon',
    dungeonBossPool: ['boss_plagueRat'],

    entryEvents: [
      { id: 'foul_stench', weight: 30, text: 'The stench is overwhelming. Disease festers in these waters.' },
      { id: 'merchant_cache', weight: 25, text: 'A smuggler\'s cache hidden in the walls!', rewards: { gold: 18 } },
      { id: 'disease_exposure', weight: 20, text: 'Foul water splashes on you!', penalties: { food: 8 } },
      { id: 'alchemist_lab', weight: 15, text: 'An abandoned alchemist lab with useful ingredients!', rewards: { herbs: 8, magic: 5 } },
      { id: 'plague_cloud', weight: 10, text: 'A cloud of spores engulfs you!', penalties: { food: 12 } },
    ],

    gatherableResources: {
      gold: { baseAmount: 4, richness: 0.6 },
      herbs: { baseAmount: 3, richness: 0.4 },
      iron: { baseAmount: 2, richness: 0.3 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  orcishStronghold: {
    id: 'orcishStronghold',
    name: 'Orcish Stronghold',
    icon: '👹',
    description: 'A fortified orc war camp. Battle-hardened warriors defend their plundered treasures.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#4a3a2a',
    minAge: 3,
    spawnWeight: 4,

    isDungeonBiome: true,
    dungeonTier: 'tier2',
    cooldownMinutes: 20,

    // Mob pool: Orc warriors and their war beasts
    dungeonMobPool: ['orcGrunt', 'orcArcher', 'orcBerserker', 'orcWarlord'],
    dungeonLootTable: 'uncommon',
    dungeonBossPool: ['boss_orcWarchief'],

    entryEvents: [
      { id: 'war_drums', weight: 30, text: 'War drums echo from within. The orcs are ready for battle.' },
      { id: 'armory_raid', weight: 25, text: 'An unguarded armory near the entrance!', rewards: { iron: 10, gold: 8 } },
      { id: 'ambush', weight: 20, text: 'Orc scouts spotted you! Prepare for resistance!', penalties: { food: 7 } },
      { id: 'trophy_room', weight: 15, text: 'A trophy room filled with plundered goods!', rewards: { gold: 14, gems: 1 } },
      { id: 'warg_attack', weight: 10, text: 'War wargs attack before you enter!', penalties: { food: 10 } },
    ],

    gatherableResources: {
      iron: { baseAmount: 4, richness: 0.6 },
      gold: { baseAmount: 3, richness: 0.5 },
      food: { baseAmount: 3, richness: 0.4 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // TIER 3 DUNGEONS - Advanced Content (Age 5+)
  // ═══════════════════════════════════════════════════════════════════════════
  corruptedTemple: {
    id: 'corruptedTemple',
    name: 'Corrupted Temple',
    icon: '🛕',
    description: 'A once-holy site now twisted by dark forces. Only the strongest dare enter.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#3a2a4a',
    minAge: 5,
    spawnWeight: 3,

    // Dungeon-specific properties
    isDungeonBiome: true,
    dungeonTier: 'tier3', // Hard - endgame content
    cooldownMinutes: 30, // 30 minutes cooldown for tier 3

    // Mob pool: BALANCED for endgame (85-90 HP, 18-20 ATK, 10× tier scaling)
    // - Orcs: Elite warriors (60→172 HP, 12→34 ATK, 6 DEF)
    // - Wraiths: Deadly ranged (45→129 HP, 15→43 ATK, range 2)
    // - Dark Knights: Heavy tanks (70→201 HP, 14→40 ATK, 8 DEF)
    // - Demons: High damage (90→258 HP, 18→51 ATK, 7 DEF)
    // - Vampires: Life drain threat (85→244 HP, 20→57 ATK, 5 DEF)
    dungeonMobPool: ['orc', 'wraith', 'darkKnight', 'demon', 'vampire'],

    // Loot table: 20% common, 40% uncommon, 30% rare, 9% epic, 1% legendary
    // Expected drops: Dragonscale armor, flaming swords, rare accessories
    dungeonLootTable: 'rare',

    // Boss: Ancient Dragon (300→861 HP, 30→85 ATK, 15 DEF, range 2)
    // Epic endgame boss - requires rare+ gear and mastery
    dungeonBossPool: ['boss_dragon'],

    entryEvents: [
      { id: 'profane', weight: 20, text: 'Sacred symbols have been defiled. Evil power radiates from within.' },
      { id: 'altar_loot', weight: 25, text: 'A corrupted altar holds valuable offerings!', rewards: { gold: 20, gems: 2, magic: 8 } },
      { id: 'dark_blessing', weight: 20, text: 'Dark energy empowers you... for a price.', rewards: { magic: 15 }, penalties: { food: 10 } },
      { id: 'guardian_statues', weight: 20, text: 'Stone guardians animate and attack!', penalties: { food: 15 } },
      { id: 'void_presence', weight: 15, text: 'Something ancient and terrible lurks below.', penalties: { food: 12, magic: 5 } },
    ],

    gatherableResources: {
      magic: { baseAmount: 5, richness: 0.9 },
      knowledge: { baseAmount: 5, richness: 0.7 },
      gems: { baseAmount: 1, richness: 0.3 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  dragonsLair: {
    id: 'dragonsLair',
    name: "Dragon's Lair",
    icon: '🐉',
    description: 'A volcanic cavern where dragon cultists worship their scaled masters. Fire and fury await.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#5a2a1a',
    minAge: 5,
    spawnWeight: 2,

    isDungeonBiome: true,
    dungeonTier: 'tier3',
    cooldownMinutes: 30,

    // Mob pool: Dragon cult and draconic creatures
    dungeonMobPool: ['dragonCultist', 'youngling', 'drakeGuard', 'elderDrake'],
    dungeonLootTable: 'rare',
    dungeonBossPool: ['boss_elderWyrm'],

    entryEvents: [
      { id: 'heat_wave', weight: 25, text: 'Waves of intense heat roll from the cavern depths.' },
      { id: 'dragon_hoard', weight: 20, text: 'Glittering treasure from the dragon hoard!', rewards: { gold: 30, gems: 3 } },
      { id: 'lava_surge', weight: 20, text: 'Lava surges near the entrance!', penalties: { food: 15 } },
      { id: 'cultist_shrine', weight: 20, text: 'A cult shrine holds arcane secrets!', rewards: { knowledge: 15, magic: 10 } },
      { id: 'dragon_breath', weight: 15, text: 'A blast of dragon fire singes you!', penalties: { food: 18 } },
    ],

    gatherableResources: {
      gold: { baseAmount: 6, richness: 0.8 },
      gems: { baseAmount: 2, richness: 0.5 },
      magic: { baseAmount: 4, richness: 0.6 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  demonicSanctum: {
    id: 'demonicSanctum',
    name: 'Demonic Sanctum',
    icon: '👿',
    description: 'A hellish portal where demons cross into our realm. Corruption incarnate.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#4a1a2a',
    minAge: 5,
    spawnWeight: 2,

    isDungeonBiome: true,
    dungeonTier: 'tier3',
    cooldownMinutes: 30,

    // Mob pool: Pure demon forces
    dungeonMobPool: ['imp', 'felGuard', 'succubus', 'demonLord'],
    dungeonLootTable: 'rare',
    dungeonBossPool: ['boss_demonPrince'],

    entryEvents: [
      { id: 'brimstone', weight: 25, text: 'The smell of brimstone chokes the air. Hell awaits.' },
      { id: 'soul_gems', weight: 20, text: 'Captured souls solidified into gems!', rewards: { gems: 4, magic: 12 } },
      { id: 'corruption', weight: 20, text: 'Demonic corruption seeps into you!', penalties: { food: 14, magic: 5 } },
      { id: 'infernal_tome', weight: 20, text: 'An infernal tome reveals dark secrets!', rewards: { knowledge: 18 } },
      { id: 'demon_ambush', weight: 15, text: 'Demons pour through a nearby portal!', penalties: { food: 16 } },
    ],

    gatherableResources: {
      magic: { baseAmount: 7, richness: 0.9 },
      gems: { baseAmount: 2, richness: 0.4 },
      knowledge: { baseAmount: 4, richness: 0.5 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  witchsCoven: {
    id: 'witchsCoven',
    name: "Witch's Coven",
    icon: '🧙‍♀️',
    description: 'A dark forest clearing where witches practice forbidden arts. Hexes and horrors abound.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#2a3a2a',
    minAge: 5,
    spawnWeight: 2,

    isDungeonBiome: true,
    dungeonTier: 'tier3',
    cooldownMinutes: 30,

    // Mob pool: Witches and their dark creations
    dungeonMobPool: ['hexedServant', 'cursedHound', 'blackWitch', 'covenMatriarch'],
    dungeonLootTable: 'rare',
    dungeonBossPool: ['boss_grandWitch'],

    entryEvents: [
      { id: 'cackles', weight: 25, text: 'Cackling echoes through the twisted trees. They know you\'re coming.' },
      { id: 'potion_stash', weight: 20, text: 'A stash of potent brews and ingredients!', rewards: { herbs: 12, magic: 8 } },
      { id: 'hex_curse', weight: 20, text: 'A hex strikes you as you enter!', penalties: { food: 12 } },
      { id: 'grimoire', weight: 20, text: 'A witch\'s grimoire filled with dark knowledge!', rewards: { knowledge: 16, magic: 6 } },
      { id: 'familiar_attack', weight: 15, text: 'Witch familiars swarm from the darkness!', penalties: { food: 14 } },
    ],

    gatherableResources: {
      herbs: { baseAmount: 6, richness: 0.8 },
      magic: { baseAmount: 5, richness: 0.7 },
      knowledge: { baseAmount: 4, richness: 0.5 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // TIER 4 DUNGEONS - Epic Content (Age 7+)
  // ═══════════════════════════════════════════════════════════════════════════
  abyssalDepths: {
    id: 'abyssalDepths',
    name: 'Abyssal Depths',
    icon: '🕳️',
    description: 'A bottomless chasm where reality frays. Only the mightiest heroes survive.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#1a0a2e',
    minAge: 7,
    spawnWeight: 2,

    // Dungeon-specific properties
    isDungeonBiome: true,
    dungeonTier: 'tier4', // Epic difficulty
    cooldownMinutes: 45, // 45 minutes cooldown for tier 4

    // Mob pool: NEW EPIC MOBS (100-150 HP, 22-28 ATK, tier4 scaling = 4.9x)
    // - Void Stalker: Fast ranged (120→588 HP, 22→108 ATK, range 2)
    // - Abyssal Knight: Tank (150→735 HP, 25→123 ATK, 15 DEF)
    // - Shadow Lord: Burst damage (110→539 HP, 28→137 ATK)
    // - Demon/Vampire: Tier 3 veterans for variety
    dungeonMobPool: ['voidStalker', 'abyssalKnight', 'shadowLord', 'demon', 'vampire'],

    // Loot table: 10% uncommon, 30% rare, 40% epic, 18% legendary, 2% mythic
    // Expected drops: Epic weapons, titanium armor, rare tech materials
    dungeonLootTable: 'epic',

    // Boss: Abyssal Lord (500→2450 HP, 40→196 ATK, 25 DEF, range 2)
    // Brutal boss - requires epic gear and consumables
    dungeonBossPool: ['boss_abyssalLord'],

    entryEvents: [
      { id: 'void_whispers', weight: 25, text: 'Whispers from the void promise power... and oblivion.' },
      { id: 'abyss_treasure', weight: 30, text: 'Ancient treasures glint in the darkness!', rewards: { gold: 40, magic: 15, gems: 3 } },
      { id: 'reality_tear', weight: 20, text: 'Reality bends around you. Strange visions cloud your mind.', rewards: { knowledge: 25 }, penalties: { food: 20 } },
      { id: 'void_touched', weight: 15, text: 'Void energy seeps into your equipment!', penalties: { food: 25, magic: 10 } },
      { id: 'cosmic_blessing', weight: 10, text: 'A fragment of cosmic power empowers you!', rewards: { magic: 30, crystals: 2 } },
    ],

    gatherableResources: {
      magic: { baseAmount: 8, richness: 1.0 },
      crystals: { baseAmount: 3, richness: 0.6 },
      artifacts: { baseAmount: 1, richness: 0.2 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  infernalFortress: {
    id: 'infernalFortress',
    name: 'Infernal Fortress',
    icon: '🔥',
    description: 'A citadel forged from hellfire and brimstone. The burning legion marshals within.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#4a1a0a',
    minAge: 7,
    spawnWeight: 1,

    isDungeonBiome: true,
    dungeonTier: 'tier4',
    cooldownMinutes: 45,

    // Mob pool: Fire demons and infernal soldiers
    dungeonMobPool: ['flameSoldier', 'infernalHound', 'lavaDemon', 'infernoKnight'],
    dungeonLootTable: 'epic',
    dungeonBossPool: ['boss_infernoLord'],

    entryEvents: [
      { id: 'hellfire', weight: 25, text: 'Pillars of hellfire illuminate the fortress walls.' },
      { id: 'burning_treasury', weight: 20, text: 'A treasury protected by flames! Worth the burns.', rewards: { gold: 50, gems: 4 } },
      { id: 'fire_trap', weight: 20, text: 'Jets of fire erupt beneath you!', penalties: { food: 22 } },
      { id: 'demon_forge', weight: 20, text: 'A demon forge with powerful weapons!', rewards: { iron: 20, magic: 15 } },
      { id: 'immolation', weight: 15, text: 'The fortress itself tries to burn you alive!', penalties: { food: 28 } },
    ],

    gatherableResources: {
      magic: { baseAmount: 9, richness: 1.0 },
      iron: { baseAmount: 6, richness: 0.8 },
      gems: { baseAmount: 3, richness: 0.5 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  shadowRealm: {
    id: 'shadowRealm',
    name: 'Shadow Realm',
    icon: '🌑',
    description: 'A dimension of pure darkness where nightmares are made manifest. Sanity frays here.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#0a0a1a',
    minAge: 7,
    spawnWeight: 1,

    isDungeonBiome: true,
    dungeonTier: 'tier4',
    cooldownMinutes: 45,

    // Mob pool: Shadow beings and nightmare creatures
    dungeonMobPool: ['shadowStalker', 'nightmareSpawn', 'sleepParalyzer', 'nightmarePrince'],
    dungeonLootTable: 'epic',
    dungeonBossPool: ['boss_nightmareKing'],

    entryEvents: [
      { id: 'darkness', weight: 25, text: 'Light itself dies here. Only darkness remains eternal.' },
      { id: 'dream_gems', weight: 20, text: 'Crystallized dreams hold immense power!', rewards: { gems: 5, magic: 20 } },
      { id: 'nightmare', weight: 20, text: 'Nightmares invade your mind!', penalties: { food: 20, magic: 8 } },
      { id: 'shadow_lore', weight: 20, text: 'Shadows whisper forbidden knowledge!', rewards: { knowledge: 30 } },
      { id: 'fear_incarnate', weight: 15, text: 'Your deepest fears manifest and attack!', penalties: { food: 25 } },
    ],

    gatherableResources: {
      magic: { baseAmount: 10, richness: 1.1 },
      crystals: { baseAmount: 4, richness: 0.7 },
      knowledge: { baseAmount: 5, richness: 0.6 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  eldritchLibrary: {
    id: 'eldritchLibrary',
    name: 'Eldritch Library',
    icon: '📚',
    description: 'An infinite library containing forbidden knowledge. Those who read too deeply go mad.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#2a2a3a',
    minAge: 7,
    spawnWeight: 1,

    isDungeonBiome: true,
    dungeonTier: 'tier4',
    cooldownMinutes: 45,

    // Mob pool: Animated books and mad scholars
    dungeonMobPool: ['animatedTome', 'inkHorror', 'madScholar', 'boundEntity'],
    dungeonLootTable: 'epic',
    dungeonBossPool: ['boss_madArchivist'],

    entryEvents: [
      { id: 'endless_shelves', weight: 25, text: 'Infinite shelves stretch in impossible directions.' },
      { id: 'rare_tomes', weight: 20, text: 'Priceless tomes filled with arcane secrets!', rewards: { knowledge: 40, magic: 18 } },
      { id: 'cursed_text', weight: 20, text: 'A cursed text burns your eyes!', penalties: { food: 18, magic: 6 } },
      { id: 'ancient_scroll', weight: 20, text: 'An ancient scroll of immense power!', rewards: { knowledge: 35, gems: 2 } },
      { id: 'madness', weight: 15, text: 'Reading too much corrupts your mind!', penalties: { food: 24 } },
    ],

    gatherableResources: {
      knowledge: { baseAmount: 12, richness: 1.2 },
      magic: { baseAmount: 8, richness: 0.9 },
      artifacts: { baseAmount: 2, richness: 0.3 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // TIER 5 DUNGEONS - Legendary Content (Age 9+)
  // ═══════════════════════════════════════════════════════════════════════════
  voidCitadel: {
    id: 'voidCitadel',
    name: 'Void Citadel',
    icon: '🌌',
    description: 'The fortress at the edge of existence. Ultimate challenge awaits the bravest.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#0a0020',
    minAge: 9,
    spawnWeight: 1,

    // Dungeon-specific properties
    isDungeonBiome: true,
    dungeonTier: 'tier5', // Legendary difficulty - ultimate challenge
    cooldownMinutes: 60, // 60 minutes cooldown for tier 5

    // Mob pool: NEW LEGENDARY MOBS (180-220 HP, 30-38 ATK, tier5 scaling = 8.3x)
    // - Cosmic Horror: Ranged devastator (180→1494 HP, 35→290 ATK, range 3)
    // - Eternity Warden: Ultimate tank (220→1826 HP, 30→249 ATK, 20 DEF)
    // - Void Emperor: Balanced elite (200→1660 HP, 38→315 ATK)
    // - Void Stalker/Shadow Lord: Tier 4 veterans as support enemies
    dungeonMobPool: ['cosmicHorror', 'eternityWarden', 'voidEmperor', 'voidStalker', 'shadowLord'],

    // Loot table: 5% rare, 25% epic, 45% legendary, 25% mythic
    // Expected drops: Legendary weapons, mythic gear, T11 tech materials
    dungeonLootTable: 'legendary',

    // Boss: The Void God (800→6640 HP, 50→415 ATK, 30 DEF, range 3)
    // Ultimate endgame boss - requires legendary+ gear and perfect play
    dungeonBossPool: ['boss_voidGod'],

    entryEvents: [
      { id: 'void_beckons', weight: 20, text: 'The void itself seems to watch your approach.' },
      { id: 'citadel_treasure', weight: 25, text: 'Otherworldly treasures materialize before you!', rewards: { gold: 80, magic: 30, gems: 5, artifacts: 2 } },
      { id: 'cosmic_download', weight: 20, text: 'Ancient cosmic knowledge floods your mind!', rewards: { knowledge: 50, science: 10 } },
      { id: 'void_corruption', weight: 20, text: 'The void begins corrupting your essence!', penalties: { food: 35, magic: 20 } },
      { id: 'eternal_fragment', weight: 15, text: 'A fragment of eternity empowers you!', rewards: { magic: 50, crystals: 5, artifacts: 1 } },
    ],

    gatherableResources: {
      magic: { baseAmount: 12, richness: 1.2 },
      crystals: { baseAmount: 5, richness: 0.8 },
      artifacts: { baseAmount: 2, richness: 0.4 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  celestialRuins: {
    id: 'celestialRuins',
    name: 'Celestial Ruins',
    icon: '👼',
    description: 'The shattered remnants of a divine realm. Corrupted angels guard forgotten treasures.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#3a3a5a',
    minAge: 9,
    spawnWeight: 1,

    isDungeonBiome: true,
    dungeonTier: 'tier5',
    cooldownMinutes: 60,

    // Mob pool: Fallen celestials and corrupted divine beings
    dungeonMobPool: ['fallenAngel', 'corruptedSeraph', 'wrathAngel', 'celestialAbomination'],
    dungeonLootTable: 'legendary',
    dungeonBossPool: ['boss_fallenSeraph'],

    entryEvents: [
      { id: 'holy_decay', weight: 20, text: 'Once-sacred halls now radiate twisted divine energy.' },
      { id: 'divine_relics', weight: 25, text: 'Divine relics of immense power!', rewards: { magic: 45, gems: 6, artifacts: 2 } },
      { id: 'holy_wrath', weight: 20, text: 'Corrupted holy light burns your soul!', penalties: { food: 30, magic: 15 } },
      { id: 'angelic_texts', weight: 20, text: 'Celestial scriptures reveal cosmic truths!', rewards: { knowledge: 55, magic: 25 } },
      { id: 'divine_judgment', weight: 15, text: 'A fallen angel passes judgment upon you!', penalties: { food: 35 } },
    ],

    gatherableResources: {
      magic: { baseAmount: 14, richness: 1.3 },
      crystals: { baseAmount: 6, richness: 0.9 },
      artifacts: { baseAmount: 3, richness: 0.5 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  dimensionalRift: {
    id: 'dimensionalRift',
    name: 'Dimensional Rift',
    icon: '💥',
    description: 'A tear in the fabric of reality. Space and time twist impossibly here.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#2a1a4a',
    minAge: 9,
    spawnWeight: 1,

    isDungeonBiome: true,
    dungeonTier: 'tier5',
    cooldownMinutes: 60,

    // Mob pool: Reality-bending horrors
    dungeonMobPool: ['realityGlitch', 'temporalAnomaly', 'dimensionalHorror', 'infiniteOne'],
    dungeonLootTable: 'legendary',
    dungeonBossPool: ['boss_realityBreaker'],

    entryEvents: [
      { id: 'reality_fracture', weight: 20, text: 'Reality fractures around you. Nothing is as it seems.' },
      { id: 'temporal_cache', weight: 25, text: 'Treasures from multiple timelines!', rewards: { gold: 100, magic: 35, artifacts: 3 } },
      { id: 'time_paradox', weight: 20, text: 'A temporal paradox damages your essence!', penalties: { food: 32, magic: 18 } },
      { id: 'dimensional_secrets', weight: 20, text: 'Secrets from beyond reality!', rewards: { knowledge: 60, crystals: 6 } },
      { id: 'reality_collapse', weight: 15, text: 'Local reality collapses around you!', penalties: { food: 38 } },
    ],

    gatherableResources: {
      magic: { baseAmount: 15, richness: 1.4 },
      crystals: { baseAmount: 7, richness: 1.0 },
      artifacts: { baseAmount: 3, richness: 0.6 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  chaosNexus: {
    id: 'chaosNexus',
    name: 'Chaos Nexus',
    icon: '🌀',
    description: 'The heart of entropy itself. Pure chaos incarnate threatens to unmake all existence.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#1a0a2a',
    minAge: 9,
    spawnWeight: 1,

    isDungeonBiome: true,
    dungeonTier: 'tier5',
    cooldownMinutes: 60,

    // Mob pool: Entities of pure chaos and entropy
    dungeonMobPool: ['chaosSpawn', 'entropyWisp', 'chaosBeast', 'entropyAvatar'],
    dungeonLootTable: 'legendary',
    dungeonBossPool: ['boss_entropySovereign'],

    entryEvents: [
      { id: 'chaos_whispers', weight: 20, text: 'The voice of chaos itself speaks to you in impossible languages.' },
      { id: 'chaos_riches', weight: 25, text: 'Order crystallizes from chaos into treasures!', rewards: { gold: 90, magic: 40, gems: 8 } },
      { id: 'entropy_drain', weight: 20, text: 'Entropy drains your life force!', penalties: { food: 35, magic: 20 } },
      { id: 'primordial_knowledge', weight: 20, text: 'Primordial knowledge of creation itself!', rewards: { knowledge: 65, artifacts: 2 } },
      { id: 'chaos_storm', weight: 15, text: 'A storm of pure chaos engulfs you!', penalties: { food: 40, magic: 15 } },
    ],

    gatherableResources: {
      magic: { baseAmount: 16, richness: 1.5 },
      crystals: { baseAmount: 8, richness: 1.1 },
      artifacts: { baseAmount: 4, richness: 0.7 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get all dungeon biomes that are available at the current age
 */
export function getDungeonBiomesByAge(age) {
  return Object.values(DUNGEON_BIOMES).filter(biome => biome.minAge <= age);
}

/**
 * Check if a biome is a dungeon biome
 */
export function isDungeonBiome(biomeId) {
  return DUNGEON_BIOMES[biomeId]?.isDungeonBiome === true;
}

/**
 * Get dungeon configuration for a biome
 */
export function getDungeonConfigForBiome(biomeId) {
  const biome = DUNGEON_BIOMES[biomeId];
  if (!biome?.isDungeonBiome) return null;

  return {
    tier: biome.dungeonTier,
    mobPool: biome.dungeonMobPool,
    lootTable: biome.dungeonLootTable,
    bossPool: biome.dungeonBossPool,
  };
}

/**
 * Get default mob/boss pools for a tier ID
 * Supports both legacy tier IDs (tier1, tier2...) and new level-based IDs (tier1_lv5, tier2_lv25...)
 */
export function getDefaultPoolsForTier(tierId) {
  // Extract base tier from ID (tier1_lv5 -> tier1, tier2 -> tier2)
  const baseTier = tierId.split('_')[0];

  // Map tier IDs to their default mob/boss pools
  const tierPools = {
    tier1: {
      mobPool: ['goblin', 'skeleton', 'wolf'],
      bossPool: ['boss_ogre'],
    },
    tier2: {
      mobPool: ['skeleton', 'wraith', 'orc', 'darkKnight'],
      bossPool: ['boss_lich'],
    },
    tier3: {
      mobPool: ['orc', 'wraith', 'darkKnight', 'demon', 'vampire'],
      bossPool: ['boss_dragon'],
    },
    tier4: {
      mobPool: ['voidStalker', 'abyssalKnight', 'shadowLord', 'demon', 'vampire'],
      bossPool: ['boss_abyssalLord'],
    },
    tier5: {
      mobPool: ['cosmicHorror', 'eternityWarden', 'voidEmperor', 'voidStalker', 'shadowLord'],
      bossPool: ['boss_voidGod'],
    },
  };

  return tierPools[baseTier] || { mobPool: ['goblin'], bossPool: ['boss_ogre'] };
}

/**
 * Get available dungeons for a player based on their level and current era
 * Returns dungeons from the DUNGEON_CONFIG.TIERS that the player can access
 */
export function getAvailableDungeons(playerLevel, currentEra) {
  // Import dynamically to avoid circular dependency
  const { DUNGEON_CONFIG } = require('../dungeon/index.js');

  return DUNGEON_CONFIG.TIERS.filter(dungeon =>
    playerLevel >= dungeon.minLevel && currentEra >= dungeon.minEra
  );
}

/**
 * Get the highest level dungeon available for a given tier
 */
export function getHighestAvailableDungeonForTier(baseTier, playerLevel, currentEra) {
  const { DUNGEON_CONFIG } = require('../dungeon/index.js');

  const tierDungeons = DUNGEON_CONFIG.TIERS.filter(d =>
    d.tier === baseTier &&
    playerLevel >= d.minLevel &&
    currentEra >= d.minEra
  );

  // Return the highest level dungeon available
  return tierDungeons.sort((a, b) => b.minLevel - a.minLevel)[0] || null;
}

export default DUNGEON_BIOMES;
